import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import Button from "../Buttons/Button";
import { cardStyles } from "../VenueCard/cardStyles";

async function copyText(text, done) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(done);
  } catch {
    toast.error("Could not copy. Select the field and copy it by hand.");
  }
}

const HIDE_AFTER_MS = 5 * 60 * 1000;

// Edit Venue: make a login for someone on the venue's team, with a temporary password. The password and
// their personal link are shown once and never stored. They must choose their own password at first sign-in.
export default function VenueTempLogin({ venueId, venueName }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [made, setMade] = useState(null);
  const current = useRef(venueId);
  const inFlight = useRef(false);

  // Another venue's page: nothing from the last one (above all the password) stays on screen.
  useEffect(() => {
    current.current = venueId;
    inFlight.current = false;
    setMade(null);
    setName("");
    setEmail("");
    setBusy(false);
  }, [venueId]);

  // The password never stays on screen: it hides itself after a few minutes.
  useEffect(() => {
    if (!made) return undefined;
    const t = setTimeout(() => setMade(null), HIDE_AFTER_MS);
    return () => clearTimeout(t);
  }, [made]);

  const make = async (e) => {
    e.preventDefault();
    if (inFlight.current) return;
    const forVenue = venueId;
    inFlight.current = true;
    setBusy(true);
    try {
      const { data } = await api.post("/venue-invites/temp-login", {
        venue_id: forVenue,
        name: name.trim(),
        email: email.trim().toLowerCase(),
      });
      if (current.current !== forVenue) return;
      setMade({ ...data, venueId: forVenue });
      setName("");
      setEmail("");
    } catch (err) {
      if (current.current === forVenue) toast.error(err?.response?.data?.message || "Could not make the login.");
    } finally {
      if (current.current === forVenue) {
        inFlight.current = false;
        setBusy(false);
      }
    }
  };

  const shown = made && made.venueId === venueId ? made : null;
  const day = (d) => {
    const t = new Date(d);
    return Number.isNaN(t.getTime()) ? "it is used" : t.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  return (
    <div style={styles.card}>
      <div style={styles.title}>Team login with a temporary password</div>
      <p style={styles.help}>
        For someone at {venueName || "the venue"} who doesn't have a ZONA login yet. You send them the password and
        their personal link; they pick their own password at first sign-in. If the email already has a ZONA account,
        use the team invite link above instead. The link doesn't include payouts; add that permission by hand after.
      </p>
      <form onSubmit={make} style={styles.row}>
        <label style={styles.field}>
          <span style={styles.fieldLabel}>Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} style={styles.input} autoComplete="off" />
        </label>
        <label style={styles.field}>
          <span style={styles.fieldLabel}>Email</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} style={styles.input} autoComplete="off" />
        </label>
        <div style={{ alignSelf: "flex-end" }}>
          <Button primaryBtn type="submit" disabled={busy || !name.trim() || !email.trim()}>
            {busy ? "Making…" : "Make login"}
          </Button>
        </div>
      </form>

      {shown && (
        <div style={{ marginTop: 16 }}>
          <div style={styles.fieldLabel}>Shown once. Copy both before leaving this page.</div>
          <div style={{ ...styles.row, marginTop: 8, alignItems: "center" }}>
            <span style={{ ...styles.fieldLabel, minWidth: 70 }}>Email</span>
            <input readOnly autoComplete="off" data-lpignore="true" value={shown.email} style={{ ...styles.input, flex: "1 1 260px" }} />
          </div>
          <div style={{ ...styles.row, marginTop: 8, alignItems: "center" }}>
            <span style={{ ...styles.fieldLabel, minWidth: 70 }}>Password</span>
            <input readOnly autoComplete="off" data-lpignore="true" value={shown.temp_password} onFocus={(e) => e.target.select()} style={{ ...styles.input, flex: "1 1 260px", fontFamily: "monospace" }} />
            <Button onClick={() => copyText(shown.temp_password, "Password copied.")}>Copy</Button>
          </div>
          <div style={{ ...styles.row, marginTop: 8, alignItems: "center" }}>
            <span style={{ ...styles.fieldLabel, minWidth: 70 }}>Link</span>
            <input readOnly value={shown.invite_url} onFocus={(e) => e.target.select()} style={{ ...styles.input, flex: "1 1 260px" }} />
            <Button onClick={() => copyText(shown.invite_url, "Link copied.")}>Copy</Button>
          </div>
          <div style={{ ...styles.fieldLabel, marginTop: 6 }}>
            For {shown.venue}: one person, link open until {day(shown.expires_at)}.
          </div>
          <button type="button" onClick={() => setMade(null)} style={styles.linkBtn}>Hide</button>
        </div>
      )}
    </div>
  );
}

const styles = {
  ...cardStyles,
  title: { fontSize: 15, fontWeight: 600, color: "#fff", marginBottom: 8 },
  linkBtn: { background: "none", border: 0, color: "rgba(255,255,255,0.6)", fontSize: 13, cursor: "pointer", padding: 0, marginTop: 10 },
};
